import { CacheProvider } from '@emotion/react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import GlobalContextProvider from 'components/GlobalContextProvider';
import { AuthProvider } from 'contexts/AuthContext';
import 'css/dropdown.css';
import 'css/global.css';
import { createApi } from 'lib/proxy';
import { configure } from 'mobx';
import { observer } from 'mobx-react';
import NextApp from 'next/app';
import Head from 'next/head';
import 'normalize.css';
import PropTypes from 'prop-types';
import '../styles/globals.css';
import createEmotionCache from 'lib/createEmotionCache';

configure({ enforceActions: 'never' });

const clientSideEmotionCache = createEmotionCache();

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
});

const App = ({ Component, emotionCache = clientSideEmotionCache, pageProps, initialAuth = {} }) => {
  const authProps = {
    initialUser: initialAuth.user,
    initialGroups: initialAuth.groups,
    initialCsrfToken: initialAuth.csrfToken,
    initialSubmissions: initialAuth.submissions,
    initialEvents: initialAuth.events,
    initialUserData: initialAuth.userData,
    isVerified: initialAuth.isVerified,
  };
  return (
    <AuthProvider {...authProps}>
      <CacheProvider value={emotionCache}>
        <GlobalContextProvider>
          <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link
              href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Righteous&family=Roboto+Condensed:ital,wght@0,100..900;1,100..900&family=Nova+Mono&display=swap"
              rel="stylesheet"
            />
          </Head>
          <ThemeProvider theme={darkTheme}>
            <Component {...pageProps} />
          </ThemeProvider>
        </GlobalContextProvider>
      </CacheProvider>
    </AuthProvider>
  );
};

App.propTypes = {
  Component: PropTypes.elementType.isRequired,
  pageProps: PropTypes.object,
  emotionCache: PropTypes.object,
  initialAuth: PropTypes.object,
};

App.defaultProps = {
  pageProps: {},
  initialAuth: {},
};

App.getInitialProps = async appContext => {
  const appProps = await NextApp.getInitialProps(appContext);

  let initialAuth = {
    user: null,
    userData: null,
    groups: [],
    submissions: [],
    events: [],
    csrfToken: '',
    isVerified: false,
  };

  let errors = null;

  if (appContext.ctx.req) {
    try {
      const api = createApi(appContext.ctx.req);
      const response = await api.get(`/api/v2/auth/status`).catch(() => ({ data: {} }));

      if (response.data.user) {
        initialAuth = {
          user: response.data.user,
          userData: response.data.userData,
          groups: response.data.groups,
          submissions: response.data.submissions,
          events: response.data.events,
          csrfToken: response.data.csrfToken,
          isVerified: response.data.isVerified,
        };
      }
    } catch (err) {
      // TODO: handle errors more gracefully
      errors = { ...err };
    }
  }

  return {
    ...appProps,
    initialAuth,
    errors,
  };
};

export default observer(App);
